/*
 * Persist cart notes in order, including edits made just before a cart refresh
 * or checkout. The shared queue survives replacement of the mini-cart footer.
 */
if (!customElements.get("cart-note")) {
  let expanded = false;
  let queue = Promise.resolve();
  let pendingWrites = 0;
  let latestNote;

  customElements.define(
    "cart-note",
    class CartNote extends HTMLElement {
      constructor() {
        super();
        this.lastSaved = null;
        this.requestedNote = null;
        this.pendingSave = Promise.resolve();
        const save = () => this.save().catch((error) => {
          this.closest("mini-cart")?.setError(error.message);
        });
        this.debouncedSave = debounce(save, 500);
        this.addEventListener("input", this.debouncedSave);
        this.addEventListener("focusout", save);
      }

      connectedCallback() {
        const textarea = this.querySelector("textarea");
        // A section response can carry the old note while a save is in flight.
        if (textarea && pendingWrites > 0 && latestNote !== undefined) {
          textarea.value = latestNote;
        }
        this.details = this.querySelector("details");
        if (!this.details) return;
        this.details.open = expanded;
        this.details.addEventListener("toggle", () => (expanded = this.details.open));
      }

      save() {
        const textarea = this.querySelector("textarea");
        if (!textarea || !this.isConnected) return queue;
        const note = textarea.value;
        if (note === this.requestedNote) return this.pendingSave;
        if (note === (this.lastSaved ?? textarea.defaultValue) && pendingWrites === 0) {
          return this.pendingSave;
        }

        this.requestedNote = note;
        latestNote = note;
        pendingWrites += 1;
        const write = async () => {
          const response = await fetch(routes.cart_update_url, {
            ...fetchConfig(),
            body: JSON.stringify({ note }),
            keepalive: true,
          });
          if (!response.ok) throw new Error(window.cartStrings?.error || "Error saving cart note");
          this.lastSaved = note;
        };
        this.pendingSave = queue.then(write).finally(() => {
          pendingWrites -= 1;
          if (this.requestedNote === note) this.requestedNote = null;
        });
        // A failed write must remain observable to checkout, but not block a retry.
        queue = this.pendingSave.catch(() => {});
        return this.pendingSave;
      }
    },
  );
}
